import { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || "https://api.bearing-intelligence.com";

const ICS_COLORS = {
  A:'#44aa66', B:'#81c784', C:'#c4a563', D:'#ff9933', E:'#ff5555', F:'#8a9bbf'
};

function daysUntil(dateStr) {
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function HypothesisCard({ hyp, onResolve }) {
  const [expanded, setExpanded] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [notes,    setNotes]    = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const days = daysUntil(hyp.resolution_date);
  const urgencyColor = days <= 7 ? '#ff5555' : days <= 30 ? '#ff9933' : '#c4a563';
  const peColor = hyp.forward_pe >= 75 ? '#ff5555' : hyp.forward_pe >= 55 ? '#ff9933' :
                  hyp.forward_pe >= 40 ? '#c4a563' : '#8a9bbf';

  const resolve = async (resolved) => {
    setLoading(true);
    await onResolve(hyp.id, resolved, notes);
    setLoading(false);
  };

  return (
    <div style={{ backgroundColor:'#0f1419', border:'1px solid rgba(196,165,99,0.2)',
      borderRadius:8, marginBottom:10, overflow:'hidden' }}>
      <div style={{ padding:'12px 16px', cursor:'pointer' }}
        onClick={() => setExpanded(e=>!e)}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
          <div style={{ flex:1 }}>
            <div style={{ display:'flex', gap:6, alignItems:'center', marginBottom:4, flexWrap:'wrap' }}>
              {hyp.is_meta && (
                <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, fontWeight:'bold',
                  backgroundColor:'rgba(196,165,99,0.15)', color:'#c4a563',
                  border:'1px solid rgba(196,165,99,0.3)', letterSpacing:'0.4px' }}>
                  META
                </span>
              )}
              {hyp.source_reliability && hyp.source_credibility && (
                <span style={{ fontSize:9, padding:'1px 6px', borderRadius:3, fontWeight:'bold',
                  backgroundColor: ICS_COLORS[hyp.source_reliability]+'22',
                  color: ICS_COLORS[hyp.source_reliability],
                  border:`1px solid ${ICS_COLORS[hyp.source_reliability]}55` }}>
                  {hyp.source_reliability}{hyp.source_credibility}
                </span>
              )}
              <span style={{ color:'#555', fontSize:10 }}>
                Briefing: {new Date(hyp.briefing_date).toLocaleDateString('en-US',
                  { month:'short', day:'numeric', year:'numeric', timeZone:'UTC' })}
              </span>
            </div>
            <div style={{ color:'#e8e9ed', fontSize:13, lineHeight:1.5 }}>{hyp.hypothesis}</div>
          </div>
          <div style={{ textAlign:'center', flexShrink:0 }}>
            <div style={{ color:peColor, fontSize:18, fontWeight:'bold' }}>{hyp.forward_pe}%</div>
            <div style={{ color:'#444', fontSize:9 }}>PE</div>
          </div>
          <span style={{ color:'#444', fontSize:10 }}>{expanded?'▲':'▼'}</span>
        </div>

        {/* Resolution countdown */}
        <div style={{ display:'flex', gap:12, marginTop:6, flexWrap:'wrap' }}>
          <span style={{ color:urgencyColor, fontSize:10, fontWeight:'bold' }}>
            {days > 0 ? `Resolves in ${days} day${days!==1?'s':''}` : days === 0 ? 'Resolves today' : `Overdue by ${Math.abs(days)} days`}
          </span>
          <span style={{ color:'#444', fontSize:10 }}>
            {new Date(hyp.resolution_date).toLocaleDateString('en-US',
              { month:'short', day:'numeric', year:'numeric' })}
          </span>
          <span style={{ color:'#555', fontSize:10 }}>
            {hyp.timeframe_basis === 'explicit' ? '📌 Explicit' :
             hyp.timeframe_basis === 'inferred' ? '🔍 Inferred' : '📅 Baseline'} timeframe
          </span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding:'0 16px 14px', borderTop:'1px solid rgba(255,255,255,0.05)' }}>
          {hyp.pe_narrative && (
            <p style={{ color:'#666', fontSize:11, lineHeight:1.6,
              fontStyle:'italic', marginTop:10, marginBottom:8 }}>
              {hyp.pe_narrative}
            </p>
          )}
          {hyp.base_rate && (
            <div style={{ marginBottom:8 }}>
              <span style={{ color:'#555', fontSize:10 }}>
                Base rate: <span style={{ color:'#8a9bbf' }}>{Math.round(hyp.base_rate*100)}%</span>
                {hyp.base_rate_basis && ` — ${hyp.base_rate_basis}`}
              </span>
            </div>
          )}

          {/* Resolution buttons */}
          {hyp.resolved === null && (
            <div style={{ marginTop:12 }}>
              {showNotes ? (
                <div>
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Resolution notes (what actually happened)..."
                    style={{ width:'100%', padding:'8px', backgroundColor:'#0a0d14',
                      border:'1px solid rgba(196,165,99,0.2)', color:'#e8e9ed',
                      borderRadius:4, fontSize:12, fontFamily:'inherit',
                      resize:'vertical', minHeight:60, marginBottom:8, boxSizing:'border-box' }}
                  />
                  <div style={{ display:'flex', gap:6 }}>
                    <button onClick={() => resolve(true)} disabled={loading}
                      style={{ flex:1, padding:'7px 0', borderRadius:4, cursor:'pointer',
                        fontSize:11, fontWeight:'bold',
                        backgroundColor:'rgba(68,170,102,0.15)', border:'1px solid #44aa66', color:'#44aa66' }}>
                      ✓ Confirmed
                    </button>
                    <button onClick={() => resolve(false)} disabled={loading}
                      style={{ flex:1, padding:'7px 0', borderRadius:4, cursor:'pointer',
                        fontSize:11, fontWeight:'bold',
                        backgroundColor:'rgba(255,85,85,0.15)', border:'1px solid #ff5555', color:'#ff5555' }}>
                      ✗ Refuted
                    </button>
                    <button onClick={() => resolve(null)} disabled={loading}
                      style={{ flex:1, padding:'7px 0', borderRadius:4, cursor:'pointer',
                        fontSize:11, fontWeight:'bold',
                        backgroundColor:'rgba(138,155,191,0.15)', border:'1px solid #8a9bbf', color:'#8a9bbf' }}>
                      ~ Inconclusive
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowNotes(true)}
                  style={{ padding:'7px 16px', borderRadius:4, cursor:'pointer',
                    fontSize:11, fontWeight:'bold',
                    backgroundColor:'rgba(196,165,99,0.1)', border:'1px solid rgba(196,165,99,0.3)',
                    color:'#c4a563' }}>
                  Resolve this hypothesis →
                </button>
              )}
            </div>
          )}

          {/* Resolution outcome */}
          {hyp.resolved !== null && hyp.resolved !== undefined && (
            <div style={{ marginTop:10, padding:'8px 10px', borderRadius:5,
              backgroundColor: hyp.resolved===true ? 'rgba(68,170,102,0.08)' :
                               hyp.resolved===false ? 'rgba(255,85,85,0.08)' : 'rgba(138,155,191,0.08)',
              border:`1px solid ${hyp.resolved===true ? '#44aa66' : hyp.resolved===false ? '#ff5555' : '#8a9bbf'}33` }}>
              <div style={{ fontSize:12, fontWeight:'bold', marginBottom:3,
                color: hyp.resolved===true ? '#44aa66' : hyp.resolved===false ? '#ff5555' : '#8a9bbf' }}>
                {hyp.resolved===true ? '✓ Confirmed' : hyp.resolved===false ? '✗ Refuted' : '~ Inconclusive'}
              </div>
              {hyp.resolution_notes && (
                <div style={{ color:'#666', fontSize:11 }}>{hyp.resolution_notes}</div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HypothesisAdmin({ token }) {
  const [hypotheses, setHypotheses] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('open');
  const [toast,      setToast]      = useState(null);

  const showToast = (msg, type='success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetch_ = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/hypotheses/open/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      let hyps = data.hypotheses || [];
      if (filter === 'open')      hyps = hyps.filter(h => h.resolved === null);
      if (filter === 'resolved')  hyps = hyps.filter(h => h.resolved !== null);
      if (filter === 'overdue')   hyps = hyps.filter(h => h.resolved === null && daysUntil(h.resolution_date) <= 0);
      setHypotheses(hyps);
    } catch { showToast('Failed to load hypotheses', 'error'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch_(); }, [filter]); // eslint-disable-line

  const handleResolve = async (id, resolved, notes) => {
    try {
      const res = await fetch(`${API_URL}/api/hypotheses/${id}/resolve`, {
        method: 'POST',
        headers: { Authorization:`Bearer ${token}`, 'Content-Type':'application/json' },
        body: JSON.stringify({ resolved, resolution_notes: notes })
      });
      if (!res.ok) throw new Error();
      showToast(resolved === true ? 'Hypothesis confirmed ✓' :
                resolved === false ? 'Hypothesis refuted ✗' : 'Marked inconclusive');
      fetch_();
    } catch { showToast('Resolution failed', 'error'); }
  };

  const openCount     = hypotheses.filter(h => h.resolved === null).length;
  const overdueCount  = hypotheses.filter(h => h.resolved === null && daysUntil(h.resolution_date) <= 0).length;

  return (
    <div>
      {toast && (
        <div style={{ position:'fixed', top:16, right:16, zIndex:1000,
          padding:'10px 16px', borderRadius:6, fontSize:13, fontWeight:'bold',
          backgroundColor: toast.type==='error' ? '#cc4444' : '#44aa66', color:'white' }}>
          {toast.msg}
        </div>
      )}

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
        <div>
          <h2 style={{ color:'#c4a563', fontFamily:'Georgia,serif', margin:'0 0 4px 0', fontSize:20 }}>
            Hypothesis Resolution
          </h2>
          <div style={{ color:'#555', fontSize:11 }}>
            {openCount} open · {overdueCount > 0 && (
              <span style={{ color:'#ff5555' }}>{overdueCount} overdue · </span>
            )}
            Resolving hypotheses updates subscriber Analyst Scores
          </div>
        </div>
        <button onClick={fetch_} style={{ background:'none',
          border:'1px solid rgba(196,165,99,0.2)', color:'#c4a563',
          cursor:'pointer', padding:'5px 10px', borderRadius:5, fontSize:11 }}>↺</button>
      </div>

      {/* Filter tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:14 }}>
        {[['open','Open'],['overdue','Overdue'],['resolved','Resolved'],['all','All']].map(([key,label]) => (
          <button key={key} onClick={() => setFilter(key)} style={{
            padding:'5px 12px', borderRadius:5, cursor:'pointer',
            fontSize:11, fontWeight:'bold',
            backgroundColor: filter===key ? 'rgba(196,165,99,0.15)' : 'transparent',
            border:`1px solid ${filter===key ? '#c4a563' : 'rgba(255,255,255,0.1)'}`,
            color: filter===key ? '#c4a563' : '#666'
          }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding:40, textAlign:'center', color:'#c4a563' }}>Loading…</div>
      ) : hypotheses.length === 0 ? (
        <div style={{ padding:40, textAlign:'center', color:'#555', fontSize:13 }}>
          {filter === 'open'
            ? "No open hypotheses. Tomorrow's briefing will generate the first batch at 0600 ET."
            : "No hypotheses match this filter."}
        </div>
      ) : (
        hypotheses.map(h => (
          <HypothesisCard key={h.id} hyp={h} onResolve={handleResolve} />
        ))
      )}
    </div>
  );
}
